import { useId, useRef, useState } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { formLabels } from '../types';
import type { DashboardDetail } from '../dashboardTypes';
import { describeFailure, type Failure } from '../drawer/failures';
import { taxPaymentsGateway } from '../index';

export const VOID_REASON_MIN = 3;
export const VOID_REASON_MAX = 500;
const LONG_NUMBER = /\d[\d\s-]{7,}\d/;

/**
 * Void Outside Payment Record (Figma S18). A required reason (3 to 500 characters) and a confirmation. Voiding never deletes the record: it stays in the audit
 * history, and the payment goes back to what the server works out from its facts (not an automatic retry). The server needs a recent authenticator-app check for
 * this; without one it answers MFA_REQUIRED and the dialog explains it and keeps what was typed.
 */
export function VoidOutsidePaymentDialog({ detail, companyName, onClose, onVoided }: { detail: DashboardDetail; companyName: string | null; onClose: () => void; onVoided: (updated: DashboardDetail) => void }) {
  const uid = useId();
  const { row } = detail;
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);

  const text = reason.trim();
  const valid = text.length >= VOID_REASON_MIN && text.length <= VOID_REASON_MAX && !LONG_NUMBER.test(text) && confirmed;

  async function submit() {
    if (submitting.current) return;
    if (text.length < VOID_REASON_MIN) return setFieldError(`Enter a reason of at least ${VOID_REASON_MIN} characters.`);
    if (text.length > VOID_REASON_MAX) return setFieldError(`The reason must be ${VOID_REASON_MAX} characters or fewer.`);
    if (LONG_NUMBER.test(text)) return setFieldError('Don’t enter an account or other long number in the reason.');
    if (!confirmed) return setFieldError('Confirm that you are authorized to void this record.');
    setFieldError(null);
    submitting.current = true;
    setBusy(true);
    setFailure(null);
    try {
      onVoided(await taxPaymentsGateway.voidOutsidePayment(row.id, { reason: text, confirmed }));
    } catch (err) {
      setFailure(describeFailure(err, 'The record couldn’t be voided. Nothing was changed. Try again.'));
      setBusy(false);
      submitting.current = false;
    }
  }

  return (
    <ModalDialog
      title="Void Outside Payment Record" size="sm" role="alertdialog" className="tpd-modal" busy={busy} onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="jr-btn tpd-btn-danger" disabled={busy || !valid} onClick={() => void submit()}>{busy ? 'Voiding…' : 'Void Record'}</button>
        </>
      )}
    >
      <p className="tpd-muted">{companyName ? `${companyName} · ` : ''}{row.agency} — {row.jurisdiction} · {formLabels[row.formCode] ?? row.formCode}</p>
      <div className="tpd-banner tpd-banner--danger" role="note">Voiding does not delete the record. The original remains visible in Audit History and the liability returns to its open state.</div>
      <div className="jr-field">
        <label htmlFor={`${uid}-reason`}>Reason for voiding <span className="jr-req" aria-hidden="true">*</span></label>
        <textarea id={`${uid}-reason`} data-autofocus rows={3} maxLength={VOID_REASON_MAX} value={reason} placeholder="E.g. Entered in error, wrong record, etc."
          aria-required="true" aria-invalid={fieldError ? true : undefined} aria-describedby={fieldError ? `${uid}-err` : undefined}
          onChange={(e) => { setReason(e.target.value); setFieldError(null); if (failure) setFailure(null); }} />
        <span className="tpd-muted" aria-live="polite">{text.length} / {VOID_REASON_MAX}</span>
        {fieldError && <p className="jr-error" id={`${uid}-err`} role="alert">{fieldError}</p>}
      </div>
      <label className="tpd-check">
        <input type="checkbox" checked={confirmed} aria-required="true" onChange={(e) => { setConfirmed(e.target.checked); setFieldError(null); }} />
        <span>I confirm I am authorized to void this outside-payment record. This action is audited.</span>
      </label>
      {failure && (
        <div className="tpd-form-error" role="alert" data-testid="void-error">
          <p>{failure.message}</p>
        </div>
      )}
    </ModalDialog>
  );
}
